/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { ATTENTION_REASONS } from "../api/onboarding";
import type { OnboardingStatus, OnboardingSummary } from "../api/onboarding";
import { REFERRAL_STAGES, type ReferralComms, type ReferralListItem, type ReferralSource, type ReferralStage } from "../api/referrals";
import type { BadgeTone } from "../components/ui/Badge";
import { listOrganizations, listProviders, organizationDetail, providerDetail } from "../api/registry";
import { localDayEndIso, localDayStartIso, type FilterField, type FilterFieldOption, type FilterLookup } from "./filters";
import { attentionCopy, isEnded, optedOutWords, STATUS_LABELS, stepLabel } from "./onboarding";
import { organizationOptions, providerName, providerOptions } from "./registry";
import { NEWEST, OLDEST, type SortChoice } from "./sorting";

/**
 * The legacy baseline bucket of the stored client status, named as if contact happened when
 * nothing has; it is never worth a word under a badge.
 */
const LEGACY_BASELINE_STATUS = "Patient Contacted";

export const SOURCE_LABELS: Record<ReferralSource, string> = {
  FAX: "Fax",
  SCREENER: "Self",
  REFERRAL_FORM: "Provider",
  CONSOLE: "Staff",
};

export const STAGE_LABELS: Record<ReferralStage, string> = {
  REFERRED: "Referred",
  PENDING_SCREENER: "Pending screener",
  SCREENER_SUBMITTED: "Screener submitted",
  CONSULT_SCHEDULED: "Consult scheduled",
  INTAKE_SCHEDULED: "Intake scheduled",
  ACTIVE: "Active",
  UNRESPONSIVE: "Unresponsive",
  UNQUALIFIED: "Unqualified",
  CLOSED: "Closed",
};

export function stageTone(stage: ReferralStage): BadgeTone {
  switch (stage) {
    case "CONSULT_SCHEDULED":
    case "INTAKE_SCHEDULED":
    case "ACTIVE":
      return "success";
    case "UNRESPONSIVE":
    case "UNQUALIFIED":
    case "CLOSED":
    case "REFERRED":
      return "neutral";
    default:
      return "info";
  }
}

/** The line under the stage badge; {@code alert} marks a hold that waits on a person. */
export type StageLine = { text: string; alert: boolean };

/**
 * The one line worth showing under a badge: a hold on the onboarding (who is needed and why, or
 * that staff paused it), else why a closed referral closed, with any channel the family opted out of
 * beside it. A family moving forward gets the badge alone; how long ago anything happened is the list's own
 * column.
 */
export function stageDetail({
  stage,
  onboarding,
  status,
}: Pick<ReferralListItem, "stage" | "onboarding" | "status">): StageLine | null {
  const reason = stage === "CLOSED" ? closeReason(onboarding, status) : null;
  const line = holdLine(onboarding) ?? (reason ? { text: reason, alert: false } : null);
  const optedOut = optedOutWords(onboarding?.optedOut ?? []);
  if (optedOut === null) {
    return line;
  }
  return line ? { ...line, text: `${line.text} · ${optedOut}` } : { text: optedOut, alert: false };
}

function holdLine(onboarding: OnboardingSummary | null): StageLine | null {
  switch (onboarding?.status) {
    case "NEEDS_CARE_TEAM": {
      const why = onboarding.attentionReason ? ` · ${attentionCopy(onboarding.attentionReason).label}` : "";
      return { text: `Needs care team${why}`, alert: true };
    }
    case "PAUSED":
      return { text: "Paused", alert: false };
    default:
      return null;
  }
}

function closeReason(onboarding: OnboardingSummary | null, status: string | null): string | null {
  return endingWords(onboarding && isEnded(onboarding.status) ? onboarding.status : status);
}

/** Why a journey ended, in words: an onboarding's status, or what the referral says. */
export function endingWords(reason: string | null): string | null {
  if (reason === null || reason === LEGACY_BASELINE_STATUS) {
    return null;
  }
  return STATUS_LABELS[reason as OnboardingStatus] ?? reason;
}

const REMINDER_COMMS = /^REMINDER_[1-9][0-9]*$/;

const FIXED_COMMS: Record<Exclude<ReferralComms, `REMINDER_${number}`>, string> = {
  REACHED_OUT: "Reached out",
  SPOKEN_TO: "Spoken to",
  COMPLETED: "Comms completed",
};

/** What has gone out to the family, in the sheet's Comm Status words; a reminder says its day. */
export function commsLabel(comms: ReferralComms): string {
  return comms in FIXED_COMMS
    ? FIXED_COMMS[comms as keyof typeof FIXED_COMMS]
    : `${comms.slice("REMINDER_".length)} day reminders sent`;
}

/** What the family does next, while the onboarding is still going. */
export function nextStep(onboarding: OnboardingSummary | null): string | null {
  return onboarding?.resumeStep && !isEnded(onboarding.status)
    ? stepLabel(onboarding.resumeStep)
    : null;
}

const organizationLookup: FilterLookup = {
  search: async (query) => {
    const result = await listOrganizations(query, 0);
    return result.ok ? organizationOptions(result.value.items) : [];
  },
  resolve: async (value) => {
    const result = await organizationDetail(value);
    return result.ok ? result.value.organization.name : null;
  },
};

const providerLookup: FilterLookup = {
  search: async (query) => {
    const result = await listProviders(query, 0);
    return result.ok ? providerOptions(result.value.items) : [];
  },
  resolve: async (value) => {
    const result = await providerDetail(value);
    return result.ok
      ? providerName(result.value.provider.firstName, result.value.provider.lastName)
      : null;
  },
};

function reminderDay(word: string): number {
  return Number(word.slice("REMINDER_".length));
}

function commsOptions(days: number[], chosen: string | null): FilterFieldOption[] {
  const words = days.map((day) => `REMINDER_${day}`);
  const reminders = new Set(chosen !== null && REMINDER_COMMS.test(chosen) ? [...words, chosen] : words);
  return (["REACHED_OUT", ...[...reminders].sort((a, b) => reminderDay(a) - reminderDay(b)), "SPOKEN_TO", "COMPLETED"] as ReferralComms[]).map(
    (value) => ({ value, label: commsLabel(value) }),
  );
}

/**
 * What staff can filter the list by. The reminder words come from the admin's schedule, in
 * {@code reminderDays}; one already in the URL stays, so a saved link keeps working once a step moves.
 */
export function referralFilterFields(reminderDays: number[], chosenComms: string | null): FilterField[] {
  return [
    {
      key: "stage",
      label: "Stage",
      kind: "choice",
      searchable: false,
      options: REFERRAL_STAGES.map((stage) => ({ value: stage, label: STAGE_LABELS[stage] })),
      scim: (value) => `stage eq "${value}"`,
    },
    {
      key: "attention",
      label: "Needs",
      kind: "choice",
      searchable: false,
      options: ATTENTION_REASONS.map((reason) => ({
        value: reason,
        label: attentionCopy(reason).label,
      })),
      scim: (value) => `attention eq "${value}"`,
    },
    {
      key: "comms",
      label: "Comms",
      kind: "choice",
      searchable: false,
      options: commsOptions(reminderDays, chosenComms),
      scim: (value) => `comms eq "${value}"`,
    },
    {
      key: "organizationId",
      label: "Organization",
      kind: "lookup",
      lookup: organizationLookup,
      scim: (value) => `organizationId eq ${Number.parseInt(value, 10)}`,
    },
    {
      key: "providerId",
      label: "Provider",
      kind: "lookup",
      lookup: providerLookup,
      scim: (value) => `providerId eq ${Number.parseInt(value, 10)}`,
    },
    {
      key: "source",
      label: "Source",
      kind: "choice",
      searchable: false,
      options: (Object.keys(SOURCE_LABELS) as ReferralSource[]).map((source) => ({
        value: source,
        label: SOURCE_LABELS[source],
      })),
      scim: (value) => `source eq "${value}"`,
    },
    {
      key: "createdAfter",
      label: "Received after",
      kind: "date",
      scim: (value) => `createdAt ge "@${localDayStartIso(value)}"`,
    },
    {
      key: "createdBefore",
      label: "Received before",
      kind: "date",
      scim: (value) => `createdAt le "@${localDayEndIso(value)}"`,
    },
  ];
}

export const REFERRAL_SORTS: SortChoice[] = [
  NEWEST,
  OLDEST,
  { value: "lastTouchpointAt:desc", label: "Recently touched", sortBy: "lastTouchpointAt", sortOrder: "desc" },
  { value: "lastTouchpointAt:asc", label: "Longest untouched", sortBy: "lastTouchpointAt", sortOrder: "asc" },
  { value: "stage:asc", label: "Stage", sortBy: "stage", sortOrder: "asc" },
];
